import { Text, Tooltip } from '@mprolab/uikit'
import dayjs from 'dayjs'
import React from 'react'
import styled, { useTheme } from 'styled-components'
import type { LicenseTerminalState } from '@mprolab/sdk'

/**
 * Whether this licence has a Terminal, and whether that Terminal is running.
 *
 * Three states, because there are three answers and collapsing any two of them
 * loses the one thing the column is for:
 *
 *   grey  — no Terminal is attached to this licence
 *   green — attached, and it checked in recently enough to be running
 *   red   — attached, but nothing has checked in for a while
 *
 * Red is not an error. It is the normal state of a licence whose owner has
 * closed their laptop, and the tooltip says so — a red dot that reads as a
 * fault sends people to support for something working exactly as intended.
 *
 * The colour is a summary; the timestamp under it is the fact. Liveness here is
 * derived from a poll, so it is always a little behind, and saying when the
 * last check-in was lets a holder judge the summary instead of trusting it.
 */

type Tone = 'online' | 'offline' | 'unbound'

const Dot = styled.span<{ $tone: Tone }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone, theme }) =>
    $tone === 'online' ? theme.colors.green : $tone === 'offline' ? theme.colors.red : theme.colors.gray6};
`

const Row = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: default;
`

export function toneOf(terminal?: LicenseTerminalState): Tone {
  if (!terminal?.bound) return 'unbound'
  return terminal.online ? 'online' : 'offline'
}

export function labelOf(tone: Tone): string {
  return tone === 'online' ? 'Online' : tone === 'offline' ? 'Offline' : 'Not connected'
}

export function explain(terminal: LicenseTerminalState | undefined, tone: Tone): string {
  if (tone === 'unbound') {
    return 'No Terminal is using this licence. Open Terminal and paste this licence’s key to connect one.'
  }
  const seen = terminal?.seenAt ? dayjs(terminal.seenAt).format('DD/MM/YYYY HH:mm') : null
  if (tone === 'online') {
    return seen
      ? `A Terminal is running on this licence. Last checked in ${seen}.`
      : 'A Terminal is running on this licence.'
  }
  // The sentence that stops a red dot reading as a fault.
  return seen
    ? `A Terminal is connected to this licence but has not checked in since ${seen} — normally because that computer is switched off or the application is closed.`
    : 'A Terminal is connected to this licence but has never checked in.'
}

const TerminalStatus: React.FC<{ terminal?: LicenseTerminalState }> = ({ terminal }) => {
  const { colors } = useTheme()
  const tone = toneOf(terminal)

  return (
    <Tooltip title={explain(terminal, tone)} customPadding="16px">
      <Row>
        <Dot $tone={tone} />
        <Text color={tone === 'unbound' ? colors.gray6 : colors.white}>{labelOf(tone)}</Text>
      </Row>
    </Tooltip>
  )
}

export default TerminalStatus
